import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { formatNotionError } from "@/lib/notionError";

// Notion Sync Visibility (2026-10, "Trifilm"/Hyderabad diagnosis) — root cause was a silent
// create-mirror failure (dal.py's create_record_mongo_first() always returns ok:true even
// when the Notion page never got created), invisible to everyone until an Edit > Save 500s
// with a generic "didn't confirm the write". This banner surfaces the SAME sync.state the
// Admin Stuck Sync panel already tracks, right where a PM/founder is already looking —
// Project Detail and the Edit modal — instead of only in a separate admin screen.
// Staff-only by construction: callers only ever pass `sync` through for NJM_ONLY_ROLES (the
// backend already strips sync.last_error/last_attempt_at for every other role, see
// modules/projects.py's _strip_staff_only_sync()), and retry itself stays founder-only,
// matching the real admin.write backend gate (kernel/permissions.py) — not a wider guess.
// Cursor fix pass (2026-10), MEDIUM #5 — visible only to staff who can actually edit a
// project (matches the `projects.create` scope used for both create AND edit, kernel/
// permissions.py), not every NJM_ONLY_ROLES member and never guest/production_partner/client.
const BANNER_VISIBLE_ROLES = new Set(["founder", "partner", "pm"]);
const VISIBLE_STATES = new Set(["pending_create", "failed_needs_attention"]);

export function NotionSyncBanner({ sync, role, recordId, onRetried, testIdPrefix = "notion-sync" }) {
  const [retrying, setRetrying] = useState(false);
  if (!BANNER_VISIBLE_ROLES.has(role)) return null;
  if (!sync || !VISIBLE_STATES.has(sync.state)) return null;
  const canRetry = role === "founder";

  const retry = async () => {
    setRetrying(true);
    try {
      const r = await api.post(`/v1/admin/stuck-sync/${recordId}/retry`, { confirm: true });
      if (r.data.ok) {
        toast.success("Created in Notion");
        onRetried?.();
      } else {
        // Notion Create Repair (2026-10), #2 — show the REAL Notion rejection when there is
        // one ("Notion rejected: <property>: <message>"), not a bare error code.
        toast.error(formatNotionError(r.data.error) || `Still not synced: ${r.data.error_reason || r.data.error || "unknown error"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code === "permission_denied" ? "Only a founder can retry this sync" : "Couldn't retry sync");
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div
      className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-tone-red-dot/30 bg-tone-red-bg px-3.5 py-2.5 text-[12.5px] text-tone-red-text"
      data-testid={`${testIdPrefix}-banner`}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          Not yet created in Notion.
          {sync.last_error && <> Last error: <span className="font-semibold">{formatNotionError(sync.last_error) || sync.last_error}</span>.</>}
          {sync.last_attempt_at && <> Last attempt {new Date(sync.last_attempt_at).toLocaleString()}.</>}
          {!canRetry && " Ask a founder to retry this."}
        </span>
      </div>
      {canRetry && (
        <button
          onClick={retry}
          disabled={retrying}
          data-testid={`${testIdPrefix}-retry-btn`}
          className="shrink-0 rounded-md border border-tone-red-dot/40 px-3 py-1.5 font-semibold hover:bg-tone-red-dot/10 disabled:opacity-50"
        >
          {retrying ? "Retrying…" : "Retry Notion sync"}
        </button>
      )}
    </div>
  );
}
